import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stack,
  Image,
} from '@chakra-ui/react';
import { ModalInfo } from '../../../components/ModalInfo';
import {
  getAllProducts,
  createProductAdmin,
  updateProductAdmin,
  deleteProductAdmin,
  // setItemAvailability,
  updateProductPositionsAdmin,
  duplicateProductAdmin,
} from '../../../api';
import ProductForm from './ProductForm';
import SortablePositionList from '../shared/SortablePositionList';
import { getMenuImageUrl, getMenuImagePlaceholderUrl } from '../../../utils/menuImageUrl';

export default function ProductList() {
  const [products, setProducts] = useState([]);
  const [filteredCategory, setFilteredCategory] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const list = await getAllProducts();
      setProducts(list);
      setOrderedProducts(list);
      setError('');
    } catch (e) {
      console.error(e);
      setError(e.message || 'Errore nel caricamento dei prodotti');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const categories = Array.from(
    new Set(products.map((p) => p.category).filter((c) => c && c.trim().length > 0)),
  ).sort();

  const visibleProducts = products.filter((p) =>
    filteredCategory ? p.category === filteredCategory : true,
  );

  const handleCreateClick = () => {
    setSelectedProduct(null);
    onFormOpen();
  };

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    onFormOpen();
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    try {
      setIsLoading(true);
      await deleteProductAdmin(productToDelete.id);
      await loadProducts();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Errore nella cancellazione del prodotto');
    } finally {
      setIsLoading(false);
      setProductToDelete(null);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      if (selectedProduct) {
        // Se l'ID è stato modificato, verifica che non esista già un altro prodotto con lo stesso ID
        if (values.id && values.id !== selectedProduct.id) {
          const exists = products.some((p) => p.id === values.id);
          if (exists) {
            throw new Error('Esiste già un prodotto con questo ID');
          }
        }
        await updateProductAdmin(selectedProduct.id, values);
      } else {
        await createProductAdmin(values);
      }
      onFormClose();
      await loadProducts();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Errore nel salvataggio del prodotto');
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handleToggleAvailability = async (product, isAvailable) => {
  //   try {
  //     setIsLoading(true);
  //     await setItemAvailability(product.id, true, isAvailable);
  //     await loadProducts();
  //   } catch (e) {
  //     console.error(e);
  //     setError(e.message || 'Errore aggiornando la disponibilità');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleDuplicate = async (product) => {
    try {
      setIsLoading(true);
      await duplicateProductAdmin(product);
      await loadProducts();
    } catch (e) {
      console.error(e);
      setError(e.message || 'Errore duplicando il prodotto');
    } finally {
      setIsLoading(false);
    }
  };

  const saveOrder = async () => {
    try {
      setIsSavingOrder(true);
      await updateProductPositionsAdmin(orderedProducts.map((p) => p.id));
      setIsReorderMode(false);
      await loadProducts();
    } catch (e) {
      console.error(e);
      setError(e.message || "Errore nel salvataggio dell'ordine");
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <Box w='100%'>
      <Stack direction='row' justify='space-between' mb={4}>
        <Select
          maxW='250px'
          placeholder='Tutte le categorie'
          value={filteredCategory}
          onChange={(e) => setFilteredCategory(e.target.value)}
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Stack direction='row'>
          <Button onClick={() => setIsReorderMode((v) => !v)} >
            {isReorderMode ? 'Chiudi riordino' : 'Riordina'}
          </Button>
          <Button onClick={handleCreateClick}>
            Nuovo prodotto
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert status='error' mb={4}>
          <AlertIcon />
          <AlertTitle mr={2}>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isReorderMode ? (
        <SortablePositionList
          items={orderedProducts
            .filter((p) => (filteredCategory ? p.category === filteredCategory : true))
            .map((p) => ({ id: p.id, title: p.title, subtitle: `${p.category} · ${p.id}` }))}
          onChangeOrder={(next) => {
            const nextIds = next.map((i) => i.id);
            const byId = new Map(orderedProducts.map((p) => [p.id, p]));
            setOrderedProducts(nextIds.map((id) => byId.get(id)).filter(Boolean));
          }}
          onSave={saveOrder}
          isSaving={isSavingOrder}
        />
      ) : (
        <Table variant='simple' size='sm'>
          <Thead>
            <Tr>
              <Th>Categoria</Th>
              <Th>Immagine</Th>
              <Th>Titolo</Th>
              <Th isNumeric>Prezzo</Th>
              <Th isNumeric>Max Q.</Th>
              {/* <Th>Disponibile</Th> */}
              <Th>Azione</Th>
            </Tr>
          </Thead>
          <Tbody>
            {visibleProducts.map((p) => (
              <Tr key={p.id}>
                <Td>{p.category}</Td>
                <Td>
                  <Image
                    src={getMenuImageUrl({
                      imageId: p.imageId,
                      id: p.id,
                      kind: 'product',
                    })}
                    fallbackSrc={getMenuImagePlaceholderUrl()}
                    boxSize='64px'
                    objectFit='cover'
                    borderRadius={6}
                    border='1px solid #444'
                    bg='#0f365f'
                  />
                </Td>
                <Td>
                  <Text isTruncated maxW='280px'>
                    {p.title}
                  </Text>
                </Td>
                <Td isNumeric>{p.price}</Td>
                <Td isNumeric>{p.maxQuantity}</Td>
                {/* <Td>
                  <Switch
                    isChecked={!!p.isAvailable}
                    onChange={(e) => handleToggleAvailability(p, e.target.checked)}
                  />
                </Td> */}
                <Td>
                  <Stack direction='row' spacing={2}>
                    <Button size='xs' onClick={() => handleEditClick(p)}>
                      Modifica
                    </Button>
                    <Button size='xs' onClick={() => handleDuplicate(p)}>
                      Duplica
                    </Button>
                    <Button size='xs' onClick={() => handleDeleteClick(p)}>
                      Elimina
                    </Button>
                  </Stack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      {visibleProducts.length === 0 && !isLoading && (
        <Text mt={4} color='white'>
          Nessun prodotto trovato.
        </Text>
      )}

      <Modal isOpen={isFormOpen} onClose={onFormClose} size='xl' closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedProduct ? 'Modifica prodotto' : 'Nuovo prodotto'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ProductForm
              initialValue={selectedProduct}
              onSubmit={handleFormSubmit}
              onCancel={onFormClose}
              isSubmitting={isSubmitting}
              existingCategories={categories}
              allProducts={products}
            />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
      <ModalInfo
        isOpen={!!productToDelete}
        title='Conferma eliminazione'
        body={
          productToDelete
            ? `Sei sicuro di voler eliminare il prodotto "${productToDelete.title}"?`
            : ''
        }
        onCancel={() => setProductToDelete(null)}
        onCancelText='Annulla'
        onClose={confirmDelete}
        onCloseText='Elimina'
      />
    </Box>
  );
}

